import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
  Paper,
  Stack,
  Avatar,
  Container,
  Fade,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Zoom,
  Alert,
  Badge
} from "@mui/material";
import {
  ArrowBack,
  EmojiEvents,
  CalendarToday,
  Description,
  CurrencyPound,
  CardGiftcard,
  Group,
  Visibility,
  Close,
  Person,
  Email,
  Star,
  Diamond,
  Paid,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader } from "../../components/CustomLoader";
import { apiJackpotService } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { capitalize } from "../../utils/helpers";
import { formatCash, formatPrice } from "../../utils/formatNumber";
import { IMAGE_BASE } from "../../utils/constants";
import CardLayout from "../../layouts/CardLayout";
import { fDate } from "../../utils/formatTime";

const ViewJackpotManagement = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageTitle, setSelectedImageTitle] = useState("");

  const fetchJackpotData = async () => {
    try {
      setLoading(true);
      const response = await apiJackpotService.getById({ id });
      setData(response?.data?.data);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (imageSrc, title) => {
    setSelectedImage(imageSrc);
    setSelectedImageTitle(title);
    setImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
    setSelectedImageTitle("");
  };

  useEffect(() => {
    if (id) fetchJackpotData();
  }, [id]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return { color: '#4caf50', bg: 'rgba(76, 175, 80, 0.1)' };
      case 'ongoing':
        return { color: '#ff9800', bg: 'rgba(255, 152, 0, 0.1)' };
      case 'announced':
        return { color: '#2196f3', bg: 'rgba(33, 150, 243, 0.1)' };
      case 'inactive':
        return { color: '#f44336', bg: 'rgba(244, 67, 54, 0.1)' };
      default:
        return { color: '#9e9e9e', bg: 'rgba(158, 158, 158, 0.1)' };
    }
  };

  const statusColors = getStatusColor(data?.status);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 5,
              borderRadius: 4,
              background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '5px',
                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 100,
                height: 100,
                background: 'radial-gradient(circle, rgba(0, 161, 221, 0.1) 0%, transparent 70%)',
                borderRadius: '50%',
              }
            }}
          >
            <Stack direction="row" alignItems="center" spacing={4}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  width: 48,
                  height: 48,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <ArrowBack sx={{ fontSize: 24 }} />
              </IconButton>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                  boxShadow: '0 12px 40px rgba(0, 161, 221, 0.4)',
                  border: '3px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <EmojiEvents sx={{ fontSize: 40, color: 'white' }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, mb: 2, lineHeight: 1.2 }}>
                  {data?.name || "Jackpot Details"}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={3}>
                  <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                    Jackpot #{data?.jackpotNumber || "N/A"}
                  </Typography>
                  <Box sx={{ width: '4px', height: '4px', backgroundColor: 'rgba(255, 255, 255, 0.5)', borderRadius: '50%' }} />
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarToday sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 20 }} />
                    <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                      {fDate(data?.drawDate)}
      </Typography>
                  </Stack>
                </Stack>
              </Box>
              <Chip
                label={capitalize(data?.status || "Unknown")}
                sx={{
                  backgroundColor: statusColors.bg,
                  color: statusColors.color,
                  fontWeight: 700,
                  fontSize: '1rem',
                  height: 40,
                  border: '2px solid',
                  borderColor: statusColors.color,
                  px: 3,
                  py: 1,
                  '&:hover': {
                    backgroundColor: statusColors.color,
                    color: 'white',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.3s ease'
                }}
              />
            </Stack>
          </Paper>

      {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Loader />
            </Box>
          ) : (
            <Grid container spacing={4}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 100%)',
                    }
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                    <Avatar 
                      sx={{ 
                        backgroundColor: 'primary.main',
                        boxShadow: '0 4px 16px rgba(0, 161, 221, 0.3)',
                        width: 48,
                        height: 48
                      }}
                    >
                      <Description sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1.25rem' }}>
                      Basic Information
                    </Typography>
                  </Stack>
                  
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                      <Stack spacing={3}>
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid',
                            borderColor: 'divider',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderColor: 'primary.main',
                            }
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                            Jackpot Name
                          </Typography>
                          <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
                            {data?.name || "N/A"}
                          </Typography>
                        </Box>
                        
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid',
                            borderColor: 'divider',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderColor: 'primary.main',
                            }
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Jackpot Number
                  </Typography>
                          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, fontFamily: 'monospace' }}>
                            #{data?.jackpotNumber || "N/A"}
                  </Typography>
                        </Box>
                      </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                      <Stack spacing={3}>
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid',
                            borderColor: 'divider',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderColor: 'primary.main',
                            }
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Draw Date
                  </Typography>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar sx={{ backgroundColor: 'secondary.main', width: 32, height: 32 }}>
                              <CalendarToday sx={{ fontSize: 16 }} />
                            </Avatar>
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                              {fDate(data?.drawDate)}
                            </Typography>
                          </Stack>
                        </Box>
                        
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid',
                            borderColor: 'divider',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderColor: statusColors.color,
                            }
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                            Status
                  </Typography>
                          <Chip
                            label={capitalize(data?.status || "Unknown")}
                            sx={{
                              backgroundColor: statusColors.bg,
                              color: statusColors.color,
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              height: 32,
                              border: '2px solid',
                              borderColor: statusColors.color,
                              '&:hover': {
                                backgroundColor: statusColors.color,
                                color: 'white',
                              }
                            }}
                          />
                        </Box>
                      </Stack>
                </Grid>

                    {/* Description */}
                <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                          border: '1px solid',
                          borderColor: 'divider',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderColor: 'primary.main',
                          }
                        }}
                      >
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Description
                  </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: 'text.secondary',
                            lineHeight: 1.8,
                            whiteSpace: "pre-wrap",
                            fontSize: '1rem',
                            fontWeight: 400
                          }}
                        >
                          {data?.description || "No description available for this jackpot."}
                  </Typography>
                      </Box>
                </Grid>
                </Grid>
                </Paper>
              </Grid>

              {/* Prize Details */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <Avatar sx={{ backgroundColor: 'success.main' }}>
                      <CardGiftcard />
                    </Avatar>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                Prize Details
                    </Typography>
                  </Stack>

                  {data?.prizeProduct?.length > 0 ? (
                    <Grid container spacing={3}>
                      {data.prizeProduct.map((product, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Card
                            sx={{
                              height: '100%',
                              backgroundColor: 'rgba(255, 255, 255, 0.02)',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 3,
                              overflow: 'hidden',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: 'primary.main',
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 40px rgba(0, 161, 221, 0.2)',
                              }
                            }}
                          >
                            <Box
                              sx={{
                                position: 'relative',
                                height: 200,
                                overflow: 'hidden',
                                background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)'
                              }}
                            >
                              {product.prizeImages?.length > 0 ? (
                                <Box
                                  component="img"
                                  src={`${IMAGE_BASE}${product.prizeImages[0]}`}
                                  alt={`Prize ${index + 1}`}
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    cursor: 'pointer',
                                    transition: 'transform 0.3s ease',
                                    '&:hover': {
                                      transform: 'scale(1.05)'
                                    }
                                  }}
                                  onClick={() => handleImageClick(`${IMAGE_BASE}${product.prizeImages[0]}`, product.prizeName)}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                  }}
                                >
                                  <CardGiftcard sx={{ fontSize: 48, color: 'text.secondary' }} />
                                </Box>
                              )}
                            </Box>
                            <CardContent>
                              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                                {product.prizeName || `Prize ${index + 1}`}
                              </Typography>
                              {product.prizeImages?.length > 1 && (
                                <Typography variant="body2" color="text.secondary">
                                  +{product.prizeImages.length - 1} more image{product.prizeImages.length - 1 > 1 ? 's' : ''}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                          </Grid>
                        ))}
                      </Grid>
                  ) : data?.prizeMoney?.length > 0 ? (
                    <Grid container spacing={3}>
                      {data.prizeMoney.map((money, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Card
                            sx={{
                              height: '100%',
                              backgroundColor: 'rgba(76, 175, 80, 0.05)',
                              border: '1px solid',
                              borderColor: 'success.main',
                              borderRadius: 3,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: 'success.dark',
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 40px rgba(76, 175, 80, 0.2)',
                              }
                            }}
                          >
                            <CardContent sx={{ textAlign: 'center', p: 3 }}>
                              <Avatar
                                sx={{
                                  width: 64,
                                  height: 64,
                                  mx: 'auto',
                                  mb: 2,
                                  backgroundColor: 'success.main',
                                  boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)'
                                }}
                              >
                                <CurrencyPound sx={{ fontSize: 32 }} />
                              </Avatar>
                              <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                                {formatCash(money?.prizeAmount || 0)}
                              </Typography>
                              <Typography variant="body1" color="text.secondary">
                      Prize Amount {index + 1}
                    </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : data?.prizeGems?.length > 0 ? (
                    <Grid container spacing={3}>
                      {data.prizeGems.map((gem, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Card
                            sx={{
                              height: '100%',
                              backgroundColor: 'rgba(156, 39, 176, 0.05)',
                              border: '1px solid',
                              borderColor: '#9c27b0',
                              borderRadius: 3,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: '#7b1fa2',
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 40px rgba(156, 39, 176, 0.2)',
                              }
                            }}
                          >
                            <CardContent sx={{ textAlign: 'center', p: 3 }}>
                              <Avatar
                                sx={{
                                  width: 64,
                                  height: 64,
                                  mx: 'auto',
                                  mb: 2,
                                  backgroundColor: '#9c27b0',
                                  boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                                }}
                              >
                                <Diamond sx={{ fontSize: 32 }} />
                              </Avatar>
                              <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                                {formatPrice(gem?.prizeAmount || 0)}
                              </Typography>
                              <Typography variant="body1" color="text.secondary">
                                Gem Prize {index + 1}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : data?.prizeSdCoins?.length > 0 ? (
                    <Grid container spacing={3}>
                      {data.prizeSdCoins.map((coin, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Card
                            sx={{
                              height: '100%',
                              backgroundColor: 'rgba(255, 193, 7, 0.05)',
                              border: '1px solid',
                              borderColor: '#ffc107',
                              borderRadius: 3,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: '#ff8f00',
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 40px rgba(255, 193, 7, 0.2)',
                              }
                            }}
                          >
                            <CardContent sx={{ textAlign: 'center', p: 3 }}>
                              <Avatar
                                sx={{
                                  width: 64,
                                  height: 64,
                                  mx: 'auto',
                                  mb: 2,
                                  backgroundColor: '#ff8f00',
                                  boxShadow: '0 8px 32px rgba(255, 193, 7, 0.3)'
                                }}
                              >
                                <Paid sx={{ fontSize: 32 }} />
                              </Avatar>
                              <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                                {formatPrice(coin?.prizeAmount || 0)}
                              </Typography>
                              <Typography variant="body1" color="text.secondary">
                                SD Coin Prize {index + 1}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Alert severity="info" sx={{ backgroundColor: 'rgba(33, 150, 243, 0.1)', color: 'white' }}>
                      No prizes configured for this jackpot
                    </Alert>
                  )}
                </Paper>
              </Grid>

              {/* Contests */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <Avatar sx={{ backgroundColor: 'warning.main' }}>
                      <Group />
                    </Avatar>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                      Associated Contests
                    </Typography>
                  </Stack>

              {data?.contestDetails?.length > 0 ? (
                <Grid container spacing={2}>
                  {data.contestDetails.map((contest, index) => (
                    <Grid item xs={12} sm={6} md={4} key={contest._id}>
                          <Card
                            sx={{
                              backgroundColor: 'rgba(255, 255, 255, 0.02)',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              p: 2,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: 'warning.main',
                                transform: 'translateY(-2px)',
                              }
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar sx={{ backgroundColor: 'warning.main', width: 32, height: 32 }}>
                                <Group sx={{ fontSize: 16 }} />
                              </Avatar>
                              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
                                {contest.contestName || "N/A"}
                        </Typography>
                            </Stack>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                    <Alert severity="info" sx={{ backgroundColor: 'rgba(33, 150, 243, 0.1)', color: 'white' }}>
                      No contests associated with this jackpot
                    </Alert>
                  )}
                </Paper>
              </Grid>

              {/* Winners */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <Avatar sx={{ backgroundColor: 'error.main' }}>
                      <Star />
                    </Avatar>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                Winners
              </Typography>
                    {data?.winners?.length > 0 && (
                      <Badge badgeContent={data.winners.length} color="primary">
                        <Typography variant="body2" color="text.secondary">
                          {data.winners.length} winner{data.winners.length > 1 ? 's' : ''}
                        </Typography>
                      </Badge>
                    )}
                  </Stack>

              {data?.winners?.length > 0 ? (
                    <Grid container spacing={3}>
                      {data.winners.map((winner, index) => {
                  const user = data?.fantasyUsers?.find(u => u._id === winner.userId);
                  return (
                          <Grid item xs={12} md={6} key={index}>
                            <Card
                              sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 3,
                                overflow: 'hidden',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  borderColor: 'error.main',
                                  transform: 'translateY(-4px)',
                                  boxShadow: '0 12px 40px rgba(244, 67, 54, 0.2)',
                                }
                              }}
                            >
                              <CardContent>
                                <Stack direction="row" spacing={3} alignItems="center">
                                  <Avatar
                                    src={user?.image ? `${IMAGE_BASE}${user.image}` : undefined}
                            sx={{
                                      width: 64,
                                      height: 64,
                                      backgroundColor: 'error.main',
                                      boxShadow: '0 4px 16px rgba(244, 67, 54, 0.3)'
                                    }}
                                  >
                                    <Person sx={{ fontSize: 32 }} />
                                  </Avatar>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                                      {user?.fullname || user?.username || "Unknown User"}
                          </Typography>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                      <Email sx={{ color: 'text.secondary', fontSize: 16 }} />
                          <Typography variant="body2" color="text.secondary">
                                        {user?.email || "N/A"}
                          </Typography>
                                    </Stack>
                                    <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 700 }}>
                                      Prize: {formatCash(winner.prize || 0)}
                          </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Alert severity="info" sx={{ backgroundColor: 'rgba(33, 150, 243, 0.1)', color: 'white' }}>
                      No winners announced yet
                    </Alert>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Image View Modal */}
          <Dialog
            open={imageModalOpen}
            onClose={handleCloseImageModal}
            maxWidth="md"
            fullWidth
            TransitionComponent={Zoom}
            sx={{
              '& .MuiDialog-paper': {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                borderRadius: 3,
                overflow: 'hidden',
              }
            }}
          >
            <DialogTitle
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Typography variant="h6">
                {selectedImageTitle}
                              </Typography>
              <IconButton
                onClick={handleCloseImageModal}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ p: 0, position: 'relative' }}>
              {selectedImage && (
                <Box
                                      sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  }}
                >
                  <img
                    src={selectedImage}
                    alt={selectedImageTitle}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
                    }}
                  />
                    </Box>
              )}
            </DialogContent>
            
            <DialogActions
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                padding: 2,
                justifyContent: 'center'
              }}
            >
              <Button
                onClick={handleCloseImageModal}
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
                  }
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Fade>
    </Container>
  );
};

export default ViewJackpotManagement;
